/**
 * Every knowledge item of the selected brand, filtered by where it stands, with the actions that make
 * new ones (from an article, or imported pairs). Opening an item shows its editor beside the list.
 */

import { useMemo, useState } from 'react';
import { FileText, Upload } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useKnowledgeItems } from '@/hooks/use-knowledge-promotion';
import { cn } from '@/lib/utils';
import { GenerateDialog } from './generate-dialog';
import { ImportDialog } from './import-dialog';
import { KnowledgeItemPanel } from './item-panel';
import { JobBanner } from './job-banner';
import { FILTERS, matchesFilter, matchesSearch, SOURCE_LABELS, stageBadge, streakText, type Filter } from './status';

export const KnowledgeItemsView = ({ canEdit }: { canEdit: boolean }) => {
	const { data: items, isLoading } = useKnowledgeItems();
	const [filter, setFilter] = useState<Filter>('all');
	const [search, setSearch] = useState('');
	const [openId, setOpenId] = useState<string | null>(null);
	const [generateOpen, setGenerateOpen] = useState(false);
	const [importOpen, setImportOpen] = useState(false);

	const shown = useMemo(
		() => (items ?? []).filter((item) => matchesFilter(item, filter) && matchesSearch(item, search)),
		[items, filter, search]
	);
	const counts = useMemo(
		() => Object.fromEntries(FILTERS.map(({ value }) => [value, (items ?? []).filter((item) => matchesFilter(item, value)).length])),
		[items]
	);
	const open = items?.find((item) => item.id === openId) ?? null;

	return (
		<div className='flex min-h-0 flex-1'>
			<div className='flex min-w-0 flex-1 flex-col gap-3'>
				<div className='flex flex-wrap items-center justify-between gap-2'>
					<Tabs value={filter} onValueChange={(value) => setFilter(value as Filter)}>
						<TabsList>
							{FILTERS.map(({ value, label }) => (
								<TabsTrigger key={value} value={value} data-testid={`filter-${value}`}>
									{label}
									<span className='ml-1 text-[10px] text-muted-foreground'>{counts[value] ?? 0}</span>
								</TabsTrigger>
							))}
						</TabsList>
					</Tabs>
					{canEdit && (
						<div className='flex gap-1.5'>
							<Button size='sm' variant='outline' onClick={() => setImportOpen(true)}>
								<Upload className='size-3.5' aria-hidden />
								Import Q&amp;A
							</Button>
							<Button size='sm' onClick={() => setGenerateOpen(true)}>
								<FileText className='size-3.5' aria-hidden />
								Generate from article
							</Button>
						</div>
					)}
				</div>
				<JobBanner />
				<Input
					placeholder='Search questions, answers, intents, articles'
					value={search}
					onChange={(event) => setSearch(event.target.value)}
				/>

				<div className='flex flex-col rounded-md border' data-testid='knowledge-items'>
					{isLoading && (
						<div className='flex flex-col gap-2 p-3'>
							<Skeleton className='h-10 w-full' />
							<Skeleton className='h-10 w-full' />
						</div>
					)}
					{!isLoading && shown.length === 0 && (
						<p className='p-4 text-sm text-muted-foreground'>
							{items?.length ? 'Nothing here.' : 'No knowledge yet. Generate it from an article, or import your question and answer pairs.'}
						</p>
					)}
					{shown.map((item) => {
						const badge = stageBadge(item);
						const streak = streakText(item);
						return (
							<button
								key={item.id}
								type='button'
								data-testid='knowledge-row'
								onClick={() => setOpenId(item.id)}
								className={cn(
									'flex items-start justify-between gap-3 border-b px-3 py-2 text-left last:border-b-0 hover:bg-muted/50',
									openId === item.id && 'bg-muted'
								)}
							>
								<span className='flex min-w-0 flex-col gap-0.5'>
									<span className='truncate text-[13px] font-medium'>{item.question || item.intent}</span>
									<span className='truncate text-[11px] text-muted-foreground'>
										{item.intent} · {SOURCE_LABELS[item.source]}
										{item.article?.name ? ` · ${item.article.name}` : ''}
									</span>
								</span>
								<span className='flex shrink-0 flex-col items-end gap-0.5'>
									<Badge variant={badge.variant} className='text-[10px]'>
										{badge.label}
									</Badge>
									{streak && <span className='text-[10px] text-muted-foreground'>{streak} sent unchanged</span>}
								</span>
							</button>
						);
					})}
				</div>
			</div>

			{open && (
				// Stays in view while the list scrolls beside it.
				<div className='sticky top-0 ml-3 h-[calc(100vh-7rem)] self-start overflow-hidden rounded-md border'>
					<KnowledgeItemPanel item={open} canEdit={canEdit} onClose={() => setOpenId(null)} />
				</div>
			)}
			<GenerateDialog open={generateOpen} onOpenChange={setGenerateOpen} />
			<ImportDialog open={importOpen} onOpenChange={setImportOpen} />
		</div>
	);
};
